import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import {
  Users, UserCheck, Clock, Calendar, AlertTriangle, Sparkles,
  Brain, Zap, ChevronDown, ChevronUp, Phone, Bot, X,
  TrendingUp, ArrowRight, DoorOpen, ScanLine, Lock, Loader2, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useAppMode } from '../../contexts/AppModeContext';
import { useAuth } from '../../contexts/AuthContext';
import { capabilitiesFor } from '../../lib/permissions';
import * as schedulerApi from '../../lib/api/schedulerApi';

const ONBOARDING_KEY = 'echo_fd_onboarding_seen';

/* ───────────────────────────────────────────────────────────
 * Demo fixtures — STRICTLY gated behind `isDemo`. Never rendered
 * in real mode; the static `banned_literals_real_mode` scanner
 * + new `real_mode_no_mock_dashboard` test ensure no literal
 * leaks reach a production user's screen.
 * ─────────────────────────────────────────────────────────── */
const DEMO_TODAYS_SCHEDULE = [
  { id: 1, patient: 'John Smith', time: '9:00 AM', type: 'Follow-up', provider: 'Dr. Chen', status: 'checked_in' },
  { id: 2, patient: 'Maria Garcia', time: '9:30 AM', type: 'New Patient', provider: 'Dr. Wilson', status: 'in_exam' },
  { id: 3, patient: 'Robert Johnson', time: '10:00 AM', type: 'Sick Visit', provider: 'Dr. Chen', status: 'waiting' },
  { id: 4, patient: 'Emily Davis', time: '10:30 AM', type: 'Follow-up', provider: 'Dr. Chen', status: 'scheduled' },
  { id: 5, patient: 'James Taylor', time: '11:00 AM', type: 'Wellness', provider: 'Dr. Wilson', status: 'scheduled' },
  { id: 6, patient: 'Sarah Williams', time: '1:00 PM', type: 'Follow-up', provider: 'Dr. Chen', status: 'scheduled' },
  { id: 7, patient: 'David Wilson', time: '2:00 PM', type: 'New Patient', provider: 'Dr. Wilson', status: 'scheduled' },
  { id: 8, patient: 'Lisa Brown', time: '3:00 PM', type: 'Sick Visit', provider: 'Dr. Chen', status: 'scheduled' },
];

const DEMO_AI_SUGGESTIONS = [
  { id: 1, icon: AlertTriangle, color: 'text-amber-600 bg-amber-50', title: 'High no-show risk', desc: 'David Wilson (2:00 PM) — 42% probability. Send reminder?', action: 'Send Reminder' },
  { id: 2, icon: Phone, color: 'text-blue-600 bg-blue-50', title: 'Callback pending', desc: 'Amanda Rodriguez requested callback at 2:00 PM', action: 'View Details' },
  { id: 3, icon: Calendar, color: 'text-emerald-600 bg-emerald-50', title: 'Slot optimization', desc: '11:30 AM open — 3 waitlist patients match this window', action: 'Fill Slot' },
  { id: 4, icon: ScanLine, color: 'text-violet-600 bg-violet-50', title: 'Intake incomplete', desc: 'Maria Garcia — missing insurance card upload', action: 'Send Form' },
];

const DEMO_DAILY_PREDICTIONS = [
  { label: 'Expected no-shows', value: '1-2' },
  { label: 'Peak hour', value: '10-11 AM' },
  { label: 'Revenue forecast', value: '$2,840' },
  { label: 'Avg wait time', value: '8 min' },
];

const DEMO_AGENT_ACTIVITY = [
  { agent: 'Front Desk Agent', action: 'Answered call from Amanda Rodriguez', time: '2 min ago', icon: Phone },
  { agent: 'Intake Agent', action: 'Processed insurance for John Smith', time: '8 min ago', icon: ScanLine },
  { agent: 'Follow-Up Agent', action: 'Sent reminders to 5 patients', time: '15 min ago', icon: Bot },
];

const STATUS_CONFIG = {
  checked_in: { label: 'Checked In', dot: '#22c55e' },
  in_exam: { label: 'In Exam', dot: '#3b82f6' },
  waiting: { label: 'Waiting', dot: '#f59e0b' },
  scheduled: { label: 'Scheduled', dot: '#d1d5db' },
};

const EchoDashboard = () => {
  const navigate = useNavigate();
  const { isDemo } = useAppMode();
  const { user } = useAuth();
  // ACL signals — single source of truth (lib/permissions.js → backend rbac.py).
  const {
    canSchedulerWrite,
    canSchedulerRead,
    canSchedulerSummary,
    canSchedulerAdmin,
  } = capabilitiesFor(user);
  // Privileged surfaces (mode-toggle, AI suggestions, predictions, agent
  // feed, Patient-Hub deep-link, Front-Desk-mode CTA) require the
  // operational write capability. Read-only / client roles see the
  // summary stats + schedule list only.
  const canSeePrivilegedCards = !!canSchedulerWrite;
  // Revenue forecast inside Daily Predictions is admin-only — keep the
  // card visible to operators but redact the $ figure for non-admins.
  const canSeeRevenue = !!canSchedulerAdmin;
  // Real mode must not show synthetic agent-activity events. Keep the
  // collapsible card in place but render an empty-state so the layout is
  // stable and tests can assert the absence of demo rows.
  const agentActivity = isDemo ? DEMO_AGENT_ACTIVITY : [];

  /* ─── REAL-MODE DATA WIRING (P0 fix) ───────────────────────────
     Demo fixtures must never reach a real-mode user. In real mode we
     fetch today's appointments from `/api/scheduler/appointments` and
     surface explicit loading / empty / error states (no silent
     fallback to fake rows). Suggestions + predictions are not yet
     backed by a live endpoint — they render an explicit empty state
     in real mode rather than fake data.
     ────────────────────────────────────────────────────────────── */
  const [appointments, setAppointments] = useState(() => (isDemo ? DEMO_TODAYS_SCHEDULE : []));
  const [apptLoading, setApptLoading] = useState(!isDemo && canSchedulerRead);
  const [apptError, setApptError] = useState(null);

  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const fetchToday = useCallback(async () => {
    if (isDemo) {
      setAppointments(DEMO_TODAYS_SCHEDULE);
      setApptLoading(false);
      return;
    }
    if (!canSchedulerRead) {
      setApptLoading(false);
      return;
    }
    setApptLoading(true);
    setApptError(null);
    const res = await schedulerApi.listAppointmentsPreferEnriched({ date: todayIso });
    if (res.ok) {
      const raw = res.data?.appointments || res.data?.items || [];
      // Normalize to the row shape the dashboard already expects.
      const norm = raw.map((a, i) => ({
        id: a.id || `apt-${i}`,
        patient: a.patient_name || a.patient || '—',
        time: a.start_label
          || (a.start ? new Date(a.start).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''),
        type: a.visit_type_name || a.visit_type || '',
        provider: a.provider_name || a.entity_name || '',
        status: (a.status === 'scheduled' || a.status === 'booked') ? 'scheduled' : (a.status || 'scheduled'),
      }));
      setAppointments(norm);
    } else {
      setApptError(res.error || { message: 'Failed to load today\'s schedule' });
      setAppointments([]);
    }
    setApptLoading(false);
  }, [isDemo, canSchedulerRead, todayIso]);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  // Suggestions + predictions: live endpoints not yet provisioned for
  // these surfaces. Real mode → empty array (rendered as explicit empty
  // state below). Demo mode → fixture set so the demo demo keeps its
  // story-telling content.
  const aiSuggestions = isDemo ? DEMO_AI_SUGGESTIONS : [];
  const dailyPredictions = isDemo ? DEMO_DAILY_PREDICTIONS : [];
  const [autopilot, setAutopilot] = useState(false);
  const [showPredictions, setShowPredictions] = useState(false);
  const [showAgentFeed, setShowAgentFeed] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(() => {
    try { return localStorage.getItem(ONBOARDING_KEY) !== 'true'; } catch { return true; }
  });

  const dismissOnboarding = () => {
    setShowOnboarding(false);
    try { localStorage.setItem(ONBOARDING_KEY, 'true'); } catch {}
  };

  const checked = appointments.filter(a => a.status === 'checked_in' || a.status === 'in_exam').length;
  const waiting = appointments.filter(a => a.status === 'waiting').length;
  const noShowRisk = appointments.filter(a => (a.noShowRisk ?? a.no_show_risk ?? 0) >= 30).length;

  return (
    <div className="space-y-5" data-testid="echo-dashboard">
      {/* ── Onboarding Overlay ── */}
      {showOnboarding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="onboarding-overlay">
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, maxWidth: 480, width: '90%', position: 'relative' }}>
            <button onClick={dismissOnboarding} style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#aaa' }} data-testid="dismiss-onboarding">
              <X style={{ width: 18, height: 18 }} />
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <DoorOpen style={{ width: 18, height: 18, color: '#fff' }} />
              </div>
              <div>
                <p style={{ fontSize: 18, fontWeight: 600, color: '#111' }}>Welcome to Echo</p>
                <p style={{ fontSize: 12, color: '#888' }}>Your front desk command center</p>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
              {[
                { icon: DoorOpen, title: 'Front Desk Mode', desc: 'Full-screen workspace to manage patient flow, check-ins, and insurance — all in one view.' },
                { icon: Brain, title: 'AI Assist & Autopilot', desc: 'AI handles calls, reminders, and intake. Toggle between Assist (approval needed) and Autopilot (fully automatic).' },
                { icon: ScanLine, title: 'Lens Scanning', desc: 'Scan IDs and insurance cards instantly. Data auto-populates into patient records.' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 7, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <item.icon style={{ width: 14, height: 14, color: '#555' }} />
                  </div>
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 500, color: '#111' }}>{item.title}</p>
                    <p style={{ fontSize: 11, color: '#888', lineHeight: 1.4 }}>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => { dismissOnboarding(); navigate('/echo/front-desk'); }}
              style={{ width: '100%', height: 42, borderRadius: 10, background: '#111', color: '#fff', fontSize: 13, fontWeight: 500, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              data-testid="onboarding-enter-fd">
              <DoorOpen style={{ width: 16, height: 16 }} /> Enter Front Desk Mode
            </button>
            <button onClick={dismissOnboarding}
              style={{ width: '100%', marginTop: 8, height: 36, borderRadius: 8, background: 'transparent', color: '#888', fontSize: 12, border: 'none', cursor: 'pointer' }}
              data-testid="onboarding-skip">
              Explore dashboard first
            </button>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] tracking-tight text-slate-900">Good morning, Front Desk</h1>
          <p className="text-slate-500 mt-1" data-testid="dashboard-appt-count">{appointments.length} appointments today &middot; {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
        </div>
        <div className="flex items-center gap-3" data-testid="dashboard-header-actions">
          {canSeePrivilegedCards ? (
            <>
              {/* Mode toggle */}
              <div className="flex items-center gap-2 p-2 px-3 rounded-lg border border-slate-200 bg-white" data-testid="mode-toggle">
                <Brain className={cn("h-4 w-4", autopilot ? "text-slate-400" : "text-slate-700")} />
                <span className={cn("text-xs font-medium", autopilot ? "text-slate-400" : "text-slate-700")}>Assist</span>
                <button
                  onClick={() => { setAutopilot(!autopilot); toast.success(autopilot ? 'Switched to Assist mode' : 'Switched to Autopilot mode'); }}
                  className={cn("relative inline-flex h-5 w-9 items-center rounded-full transition-colors", autopilot ? "bg-violet-600" : "bg-slate-200")}
                  data-testid="autopilot-switch">
                  <span className={cn("inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform", autopilot ? "translate-x-4.5" : "translate-x-0.5")} style={{ transform: autopilot ? 'translateX(18px)' : 'translateX(2px)' }} />
                </button>
                <span className={cn("text-xs font-medium", autopilot ? "text-slate-700" : "text-slate-400")}>Autopilot</span>
                <Zap className={cn("h-4 w-4", autopilot ? "text-amber-500" : "text-slate-400")} />
              </div>
              {/* Enter Front Desk Mode */}
              <button onClick={() => navigate('/echo/front-desk')}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-medium transition-all hover:opacity-90"
                style={{ background: '#111' }}
                data-testid="enter-front-desk-btn">
                <DoorOpen className="h-4 w-4" /> Enter Front Desk Mode
              </button>
            </>
          ) : (
            <Badge
              variant="outline"
              className="gap-1.5 px-2.5 py-1 text-[10px] border-slate-200 text-slate-500"
              data-testid="dashboard-readonly-badge"
            >
              <Lock className="h-3 w-3" /> Read-only view
            </Badge>
          )}
        </div>
      </div>

      {/* ── Patient Hub Quick Access (privileged) ── */}
      {canSeePrivilegedCards && (
        <button
          onClick={() => window.open('/patient-hub/checkin', '_blank')}
          className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg border transition-colors hover:bg-slate-50"
          style={{ background: '#fff', border: '0.5px solid #ebebeb', cursor: 'pointer' }}
          data-testid="patient-hub-btn"
        >
        <div className="flex items-center gap-2.5">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><rect x="0.5" y="0.5" width="4" height="4" rx="0.5" stroke="#555" /><rect x="7" y="0.5" width="4" height="4" rx="0.5" stroke="#555" /><rect x="0.5" y="7" width="4" height="4" rx="0.5" stroke="#555" /><rect x="7" y="7" width="4" height="4" rx="0.5" stroke="#555" /></svg>
          <span style={{ fontSize: 13, fontWeight: 500, color: '#111' }}>Patient Hub — open check-in link</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#22c55e' }} />
          <span style={{ fontSize: 9, color: '#22c55e', fontWeight: 500 }}>Live</span>
        </div>
      </button>
      )}

      {/* ── Stat Cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Today', value: appointments.length, icon: Calendar, color: 'text-slate-600 bg-slate-100' },
          { label: 'Checked In', value: checked, icon: UserCheck, color: 'text-emerald-600 bg-emerald-50' },
          { label: 'Waiting', value: waiting, icon: Clock, color: 'text-amber-600 bg-amber-50' },
          { label: 'No-show Risk', value: noShowRisk, icon: AlertTriangle, color: 'text-red-600 bg-red-50' },
        ].map((s, i) => (
          <Card key={i} className="border-slate-200" data-testid={`stat-card-${i}`}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className={cn("h-10 w-10 rounded-xl flex items-center justify-center", s.color.split(' ')[1])}>
                <s.icon className={cn("h-5 w-5", s.color.split(' ')[0])} />
              </div>
              <div>
                <p className="text-2xl font-bold font-['Plus_Jakarta_Sans']">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── Two-Column Layout ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: Today's Schedule (condensed) */}
        <div className="lg:col-span-2">
          <Card className="border-slate-200" data-testid="dashboard-schedule-card">
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-slate-500" />
                <p className="text-sm font-semibold text-slate-800">Today's Schedule</p>
                <Badge className="bg-slate-100 text-slate-600 text-[10px] border-0">{appointments.length}</Badge>
              </div>
              <div className="flex items-center gap-2">
                {!isDemo && (
                  <button
                    onClick={fetchToday}
                    disabled={apptLoading}
                    className="text-slate-500 hover:text-slate-700 transition-colors"
                    data-testid="dashboard-schedule-refresh"
                    aria-label="Refresh today's schedule"
                  >
                    <RefreshCw className={cn("h-3 w-3", apptLoading && "animate-spin")} />
                  </button>
                )}
                <button onClick={() => navigate('/echo/schedule')} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors" data-testid="view-full-schedule">
                  View Full <ArrowRight className="h-3 w-3" />
                </button>
              </div>
            </div>
            <CardContent className="p-0 pb-2">
              {apptLoading && appointments.length === 0 ? (
                <div className="flex items-center justify-center py-8" data-testid="dashboard-schedule-loading">
                  <Loader2 className="h-4 w-4 animate-spin text-slate-400 mr-2" />
                  <span className="text-[11px] text-slate-500">Loading today's schedule…</span>
                </div>
              ) : apptError ? (
                <div className="px-5 py-6 text-center" data-testid="dashboard-schedule-error">
                  <AlertTriangle className="h-5 w-5 text-red-400 mx-auto mb-1.5" />
                  <p className="text-[11px] text-red-700 font-medium">Could not load today's schedule</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">{apptError.message || 'Server error — please retry.'}</p>
                  <button onClick={fetchToday} className="mt-2 text-[10px] text-slate-600 underline" data-testid="dashboard-schedule-retry">Retry</button>
                </div>
              ) : appointments.length === 0 ? (
                <div className="px-5 py-8 text-center" data-testid="dashboard-schedule-empty">
                  <Calendar className="h-5 w-5 text-slate-300 mx-auto mb-1.5" />
                  <p className="text-[11px] font-medium text-slate-600">No appointments today</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    {canSchedulerWrite
                      ? 'Open the scheduler to book a new appointment.'
                      : 'Your provider hasn\'t booked any visits for today.'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-slate-50">
                  {appointments.map((apt) => {
                    const st = STATUS_CONFIG[apt.status] || STATUS_CONFIG.scheduled;
                    return (
                      <div key={apt.id} className="flex items-center gap-3 px-5 py-2.5 hover:bg-slate-50/50 transition-colors" data-testid={`schedule-row-${apt.id}`}>
                        <div style={{ width: 7, height: 7, borderRadius: '50%', background: st.dot, flexShrink: 0 }} />
                        <span className="text-xs font-medium text-slate-500 w-16 shrink-0">{apt.time}</span>
                        <span className="text-xs font-medium text-slate-800 flex-1 truncate">{apt.patient}</span>
                        <span className="text-[10px] text-slate-400 shrink-0">{apt.type}</span>
                        <span className="text-[10px] text-slate-400 shrink-0 hidden sm:inline">{apt.provider}</span>
                        <span style={{ fontSize: 9, fontWeight: 500, padding: '1px 7px', borderRadius: 10, background: apt.status === 'checked_in' || apt.status === 'in_exam' ? '#f0fdf4' : apt.status === 'waiting' ? '#fffbeb' : '#f5f5f5', color: apt.status === 'checked_in' || apt.status === 'in_exam' ? '#16a34a' : apt.status === 'waiting' ? '#d97706' : '#999' }}>{st.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          {/* AI Suggestions — privileged (operational triage actions) */}
          {canSeePrivilegedCards && (
          <Card className="border-slate-200" data-testid="ai-suggestions-card">
            <div className="flex items-center gap-2 px-4 pt-4 pb-2">
              <Sparkles className="h-4 w-4 text-violet-500" />
              <p className="text-sm font-semibold text-slate-800">AI Suggestions</p>
            </div>
            <CardContent className="pt-0 space-y-2">
              {aiSuggestions.length === 0 ? (
                <div className="px-2 py-4 text-center" data-testid="ai-suggestions-empty">
                  <Sparkles className="h-4 w-4 text-slate-300 mx-auto mb-1" />
                  <p className="text-[11px] text-slate-500">No active suggestions</p>
                  <p className="text-[9px] text-slate-400 mt-0.5">AI insights will appear here once the recommendation engine is connected.</p>
                </div>
              ) : aiSuggestions.map((s) => (
                <div key={s.id} className="flex items-start gap-2.5 p-2.5 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors" data-testid={`suggestion-${s.id}`}>
                  <div className={cn("h-6 w-6 rounded-md flex items-center justify-center shrink-0 mt-0.5", s.color.split(' ')[1])}>
                    <s.icon className={cn("h-3 w-3", s.color.split(' ')[0])} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-semibold text-slate-800">{s.title}</p>
                    <p className="text-[10px] text-muted-foreground leading-relaxed">{s.desc}</p>
                    <button className="text-[10px] font-medium text-violet-600 hover:text-violet-700 mt-1" onClick={() => toast.success(`${s.action} triggered`)} data-testid={`suggestion-action-${s.id}`}>
                      {s.action} &rarr;
                    </button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
          )}

          {/* Daily Predictions (collapsible). Revenue line redacted for non-admins. */}
          {canSchedulerSummary && (
          <Card className="border-slate-200" data-testid="daily-predictions-card">
            <button onClick={() => setShowPredictions(p => !p)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50/50 transition-colors" data-testid="toggle-predictions">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-emerald-500" />
                <p className="text-sm font-semibold text-slate-800">Daily Predictions</p>
              </div>
              {showPredictions ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
            </button>
            {showPredictions && (
              <CardContent className="pt-0 pb-3">
                {dailyPredictions.length === 0 ? (
                  <div className="text-center py-3" data-testid="daily-predictions-empty">
                    <p className="text-[11px] text-slate-500">No prediction data yet</p>
                    <p className="text-[9px] text-slate-400 mt-0.5">Predictions appear once enough operational history is collected for your tenant.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {dailyPredictions.map((p, i) => {
                      const isRevenue = /revenue/i.test(p.label);
                      return (
                        <div key={i} className="flex items-center justify-between" data-testid={`prediction-${i}`}>
                          <span className="text-[11px] text-slate-500">{p.label}</span>
                          {isRevenue && !canSeeRevenue ? (
                            <span
                              className="text-xs font-medium text-slate-400 inline-flex items-center gap-1"
                              data-testid="prediction-revenue-redacted"
                            >
                              <Lock className="h-3 w-3" /> admin only
                            </span>
                          ) : (
                            <span className="text-xs font-bold text-slate-800">{p.value}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            )}
          </Card>
          )}

          {/* Agent Activity (collapsible) — privileged */}
          {canSeePrivilegedCards && (
          <Card className="border-slate-200" data-testid="agent-activity-card">
            <button onClick={() => setShowAgentFeed(p => !p)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50/50 transition-colors" data-testid="toggle-agent-feed">
              <div className="flex items-center gap-2">
                <Bot className="h-4 w-4 text-slate-500" />
                <p className="text-sm font-semibold text-slate-800">Agent Activity</p>
                {autopilot && <Badge className="bg-amber-100 text-amber-700 text-[9px] border-0">Autopilot</Badge>}
              </div>
              {showAgentFeed ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
            </button>
            {showAgentFeed && (
              <CardContent className="pt-0 pb-3 space-y-2.5" data-testid="agent-feed-body">
                {agentActivity.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-4 text-center" data-testid="agent-feed-empty">
                    <div className="h-7 w-7 rounded-full bg-slate-50 flex items-center justify-center mb-1.5">
                      <Bot className="h-3.5 w-3.5 text-slate-300" />
                    </div>
                    <p className="text-[11px] text-slate-500">No agent activity yet</p>
                    <p className="text-[9px] text-slate-400 mt-0.5">Agent events will appear here once your agents start running.</p>
                  </div>
                ) : (
                  agentActivity.map((a, i) => (
                    <div key={i} className="flex items-start gap-2" data-testid={`agent-activity-${i}`}>
                      <div className="h-5 w-5 rounded-full bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                        <a.icon className="h-2.5 w-2.5 text-slate-500" />
                      </div>
                      <div>
                        <p className="text-[11px] text-slate-700"><span className="font-medium">{a.agent}</span> {a.action}</p>
                        <p className="text-[9px] text-muted-foreground">{a.time}</p>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            )}
          </Card>
          )}

          {/* Read-only / client roles: friendly limited-view footer so the
              right column never collapses to empty. */}
          {!canSeePrivilegedCards && canSchedulerRead && (
            <Card className="border-dashed border-slate-200" data-testid="dashboard-limited-view">
              <CardContent className="p-4 text-center">
                <Lock className="h-4 w-4 text-slate-400 mx-auto mb-1.5" />
                <p className="text-[11px] font-medium text-slate-600">Limited dashboard</p>
                <p className="text-[10px] text-slate-400 mt-0.5">Operational cards (AI suggestions, agent feed, predictions) are restricted to operator / admin roles.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default EchoDashboard;
